import { useMutation, useQuery, gql } from '@apollo/client';
import get from 'lodash/get';
import { useCallback } from 'react';

import {
  EnableInboxLanguageOverrideInput,
  UpdatePhoneLineLanguageSettingsInput,
} from '../graphql';
import { GET_PHONELINE_BY_ID } from '../queries/gql/getPhonelineById.gql';

export const useGetPhoneLineById = (phonelineId: string) => {
  const { data, loading } = useQuery(GET_PHONELINE_BY_ID, {
    variables: { phonelineId: phonelineId },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const phoneline = get(data, 'phoneline');

  return { phoneline, loading };
};

const UPDATE_PHONELINE_LANGUAGE_SETTINGS_MUTATION = gql(`
  mutation updatePhoneLineLanguageSettings(
    $input: UpdatePhoneLineLanguageSettingsInput!
  ) {
    updatePhoneLineLanguageSettings(input: $input) {
      phoneline {
        id
        speechLanguage
        allowedSpeechLanguages
      }
    }
  }
`);

const ENABLE_INBOX_LANGUAGE_OVERRIDE_MUTATION = gql(`
  mutation enableInboxLanguageOverride(
    $input: EnableInboxLanguageOverrideInput!
  ) {
    enableInboxLanguageOverride(input: $input) {
      phoneline {
          id
          overridesInboxLanguage
          speechLanguage
          allowedSpeechLanguages
      }
    }
  }
`);

export const usePhoneLineLanguageSettingsMutation = () => {
  const [updateLanguageSettings] = useMutation(
    UPDATE_PHONELINE_LANGUAGE_SETTINGS_MUTATION,
  );

  return useCallback(
    (input: UpdatePhoneLineLanguageSettingsInput) =>
      updateLanguageSettings({
        variables: { input },
        refetchQueries: [
          {
            query: GET_PHONELINE_BY_ID,
            variables: { phonelineId: input.phonelineId },
          },
        ],
      }),
    [updateLanguageSettings],
  );
};

export const useEnableInboxLanguageOverrideMutation = () => {
  const [enableInboxLanguageOverride] = useMutation(
    ENABLE_INBOX_LANGUAGE_OVERRIDE_MUTATION,
  );

  return useCallback(
    (input: EnableInboxLanguageOverrideInput) =>
      enableInboxLanguageOverride({
        variables: { input },
        refetchQueries: [
          {
            query: GET_PHONELINE_BY_ID,
            variables: { phonelineId: input.phonelineId },
          },
        ],
      }),
    [enableInboxLanguageOverride],
  );
};

export const GET_PHONELINE_WITH_DEPARTMENT = gql`
  query GetPhonelineWithDepartment($phonelineId: String!) {
    phoneline(phonelineId: $phonelineId) {
      id
      department {
        id
        name
      }
      place {
        placeGroupId
      }
    }
  }
`;

export const useGetPhonelineWithDepartment = (phonelineId: string) => {
  const { data, loading, error } = useQuery(GET_PHONELINE_WITH_DEPARTMENT, {
    variables: { phonelineId },
    skip: !phonelineId,
  });

  return {
    phoneline: data?.phoneline,
    departmentId: data?.phoneline?.department?.id,
    placeGroupId: data?.phoneline?.place?.placeGroupId,
    loading,
    error,
  };
};
